import { Box, Stack } from '@mui/material';
import { HChart } from 'app/concierge/types';
import Chart from 'components/chart/Chart';
import { UserAuth } from 'store/auth';
import { useAppSelector } from 'store/hooks';
import { useMemo } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import ExpandButton from './expand-button';

interface IProps {
    hChartConfig: HChart;
    conciergeResponse: React.ReactNode;
    isStandalone?: boolean;
}

export default function JsonHChart({ hChartConfig, conciergeResponse, isStandalone }: IProps) {
    const { width, ref } = useResizeDetector();

    const userAuth: UserAuth = useAppSelector((state) => state.auth);
    const brandThemeProperties = userAuth?.userInfo?.brandThemeProperties ?? {};

    const cleanJsonString = (jsonString: string): string => {
        return jsonString.replace(/"formatter"\s*:\s*function\s*\([^)]*\)\s*\{[^}]*\}/g, '"formatter":null');
    };

    let processedOptions: any;
    try {
        if (typeof hChartConfig.data.highChartsOptions === 'string') {
            const cleanedString = cleanJsonString(hChartConfig.data.highChartsOptions);
            processedOptions = JSON.parse(cleanedString);
        } else {
            processedOptions = { ...hChartConfig.data.highChartsOptions };
        }
    } catch (error) {
        console.error('Failed to parse chart options:', error);
        processedOptions = {
            chart: { type: 'column' },
            title: { text: 'Chart data parsing error' },
            series: [],
        };
    }

    const { barColors, lineColors } = useMemo(() => {
        const sorted = [...brandThemeProperties.charting_colors].sort((a, b) => a.display_order - b.display_order);

        return {
            barColors: sorted.map((c) => c.bar_color),
            lineColors: sorted.map((c) => c.line_color),
        };
    }, [brandThemeProperties.charting_colors]);

    delete processedOptions.navigator;
    delete processedOptions.rangeSelector;
    delete processedOptions.scrollbar;
    if (processedOptions.chart) {
        if (isStandalone) {
            processedOptions.chart.height = window.innerHeight;
            processedOptions.chart.width = window.innerWidth;
        } else {
            processedOptions.chart.height = width ? Math.round(width * 0.9) : undefined;
        }
    }

    const cleanFormatters = (obj: any) => {
        if (!obj || typeof obj !== 'object') return;

        if (obj.formatter && typeof obj.formatter !== 'function') {
            delete obj.formatter;
        }

        Object.keys(obj).forEach((key) => {
            if (typeof obj[key] === 'object' && obj[key] !== null) {
                cleanFormatters(obj[key]);
            }
        });
    };

    cleanFormatters(processedOptions);

    if (processedOptions.xAxis) {
        if (Array.isArray(processedOptions.xAxis)) {
            processedOptions.xAxis.forEach((axis: any) => {
                if (axis.labels) {
                    axis.labels.formatter = '';
                }
            });
        } else {
            if (processedOptions.xAxis.labels) {
                processedOptions.xAxis.labels.formatter = '';
            }
        }
    }

    if (processedOptions.yAxis) {
        if (Array.isArray(processedOptions.yAxis)) {
            processedOptions.yAxis.forEach((axis: any) => {
                if (axis.labels) {
                    axis.labels.formatter = '';
                }
            });
        } else {
            if (processedOptions.yAxis.labels) {
                processedOptions.yAxis.labels.formatter = '';
            }
        }
    }

    processedOptions.tooltip = {};
    processedOptions.colors = barColors;

    if (isStandalone) {
        return (
            <Chart
                afterChartCreated={() => {
                    document.body.classList.add('component-ready');
                }}
                chartOptions={processedOptions}
                constructorType={'chart'}
            />
        );
    }

    return (
        <Stack sx={{ justifyContent: 'center', alignItems: 'center' }}>
            <Box
                ref={ref}
                sx={{
                    width: '100%',
                    paddingRight: '40px',
                    paddingTop: '5px',
                    position: 'relative',
                }}
            >
                <Chart chartOptions={processedOptions} constructorType={'mapChart'} />
            </Box>
            <ExpandButton
                conciergeMessage={conciergeResponse}
                content={
                    <Box>
                        <Chart chartOptions={processedOptions} constructorType={'mapChart'} />
                    </Box>
                }
            />
        </Stack>
    );
}
